
import { supabase } from '../../integrations/supabase/client';

export interface FileUploadResult {
  success: boolean;
  url?: string;
  error?: string;
}

// Ensure storage bucket exists
export const ensureStorageBucket = async (): Promise<void> => {
  try {
    console.log('📦 STORAGE: Checking if target-images bucket exists...');
    
    const { data: buckets, error: listError } = await supabase.storage.listBuckets();
    
    if (listError) {
      console.error('❌ STORAGE: Error listing buckets:', listError);
      return;
    }
    
    const bucketExists = buckets?.some(bucket => bucket.name === 'target-images');
    
    if (!bucketExists) {
      console.log('📦 STORAGE: Creating target-images bucket...');
      const { error: createError } = await supabase.storage.createBucket('target-images', {
        public: true,
        allowedMimeTypes: ['image/png', 'image/jpeg', 'image/jpg', 'image/gif', 'image/webp'],
        fileSizeLimit: 10485760 // 10MB
      });
      
      if (createError) {
        console.error('❌ STORAGE: Error creating bucket:', createError);
      } else {
        console.log('✅ STORAGE: target-images bucket created successfully');
      }
    } else {
      console.log('✅ STORAGE: target-images bucket already exists');
    }
  } catch (error) {
    console.error('❌ STORAGE: Exception checking bucket:', error);
  }
};

export const uploadFileToSupabase = async (
  file: File,
  userId: string,
  targetCode?: string
): Promise<FileUploadResult> => {
  try {
    console.log('📦 STORAGE: Starting file upload:', { 
      fileName: file.name, 
      size: file.size, 
      type: file.type,
      targetCode,
      userId: userId.substring(0, 8) + '...' // Partial for privacy
    });
    
    // Ensure bucket exists before upload
    await ensureStorageBucket();
    
    // Create a unique file path
    const fileExt = file.name.split('.').pop();
    const fileName = `${Date.now()}-${Math.random().toString(36).substring(2)}.${fileExt}`;
    const filePath = targetCode 
      ? `${userId}/${targetCode}/${fileName}`
      : `${userId}/chat/${fileName}`;

    console.log('📦 STORAGE: Generated file path:', filePath);

    // Upload file to storage
    console.log('📦 STORAGE: Uploading to bucket "target-images"...');
    const { data, error } = await supabase.storage
      .from('target-images')
      .upload(filePath, file, {
        cacheControl: '3600',
        upsert: false
      });

    if (error) {
      console.error('❌ STORAGE: Upload error:', error);
      return { success: false, error: error.message };
    }

    console.log('✅ STORAGE: Upload successful:', {
      path: data.path,
      fullPath: data.fullPath
    });

    // Get public URL
    console.log('📦 STORAGE: Generating public URL...');
    const { data: urlData } = supabase.storage
      .from('target-images')
      .getPublicUrl(data.path);

    console.log('✅ STORAGE: Public URL generated:', {
      publicUrl: urlData.publicUrl,
      path: data.path
    });
    
    return { 
      success: true, 
      url: urlData.publicUrl 
    };
  } catch (error) {
    console.error('❌ STORAGE: Exception during upload:', {
      error: error,
      message: error instanceof Error ? error.message : 'Unknown error',
      stack: error instanceof Error ? error.stack : undefined
    });
    return { 
      success: false, 
      error: error instanceof Error ? error.message : 'Upload failed' 
    };
  }
};
